import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { commitKeys, taskKeys } from '../queries/keys';
import { ALL_MODALITIES, assignmentPaths, commitPaths, taskPaths } from '../queries/paths';
import { breakdownKeys } from '../../breakdown/queries/keys';

// TSS vite/03 — server state for the `tasks` entity.
//
// Reading and writing the record, and nothing else. Claiming a task and
// reviewing a delivery are not CRUD: they are the atomic reservation of tier 2
// and they have their own permissions (tasks:claim, tasks:review). They do not
// belong in an admin form.

export const TASKS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// The richest filter set in the API: repository_id, tier_id, project_id,
// workflow_state and kind, on top of limit/offset.
export function useTasksList({
    limit = TASKS_PAGE_SIZE, offset = 0, tierId, projectId, repositoryId, workflowState, kind,
} = {}) {
    // Both modalities: see ALL_MODALITIES in queries/paths.js.
    const params = { limit, offset, modality: ALL_MODALITIES };
    if (tierId) params.tier_id = tierId;
    if (projectId) params.project_id = projectId;
    if (repositoryId) params.repository_id = repositoryId;
    if (workflowState) params.workflow_state = workflowState;
    if (kind) params.kind = kind;

    return useQuery({
        queryKey: taskKeys.list(params),
        queryFn: ({ signal }) => api.get(taskPaths.list(), { params, signal }).then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

export function useTask(id) {
    return useQuery({
        queryKey: taskKeys.detail(id),
        queryFn: ({ signal }) => api.get(taskPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function useCreateTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(taskPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
    });
}

export function useUpdateTask(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(taskPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: taskKeys.detail(id) });
            qc.invalidateQueries({ queryKey: taskKeys.all });
        },
    });
}

export function useUnblockTask(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (reason) => api.post(taskPaths.unblock(id), { reason }).then(r => r.data),
        // This endpoint has no idempotency receipt. Never resend automatically,
        // and reconcile even when the response was lost or the state changed.
        retry: false,
        onSettled: () => {
            invalidateTask(qc, id);
            qc.invalidateQueries({ queryKey: breakdownKeys.all });
        },
    });
}

// ------------------------------------------------- directing work (T03)
//
// ASSIGNMENT, RESERVATION AND WEIGHT ARE THREE THINGS, and none of these calls
// touches the other two:
//
//   assigned_to   whose work it is            moved from here
//   claimed_*     which session has it NOW    moved by the claim, the delivery
//                                             and the review — never from here
//   priority      the order within its group  moved from here, by its own route
//
// Changing weight or assignment does NOT interrupt anything: it does not
// release a live reservation, does not move `workflow_state`, does not touch
// `claimed_until`. It affects the NEXT selections. Somebody in the middle of a
// task cannot lose it because another person moved a number.
//
// All three are idempotent: the same value answers 200 with `changed: false`
// and writes no event.

export function useAssignTask(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (userId) => api
            .put(assignmentPaths.assignment(id), { user_id: Number(userId) })
            .then(r => r.data),
        onSuccess: () => invalidateTask(qc, id),
    });
}

export function useUnassignTask(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.delete(assignmentPaths.assignment(id)).then(r => r.data),
        onSuccess: () => invalidateTask(qc, id),
    });
}

// The weight, through its own route so the change is recorded as a change of
// priority and not as a side effect of an edit.
export function useChangeWeight(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (priority) => api
            .patch(assignmentPaths.weight(id), { priority: Number(priority) })
            .then(r => r.data),
        onSuccess: () => invalidateTask(qc, id),
    });
}

function invalidateTask(qc, id) {
    qc.invalidateQueries({ queryKey: taskKeys.detail(String(id)) });
    qc.invalidateQueries({ queryKey: taskKeys.all });
    // Each one writes its event, and the pulse is showing events.
    qc.invalidateQueries({ queryKey: ['events'] });
}

// From a commit hash back to the plan.
//
// A HASH IS NOT AN ID. It travels as text and is normalised to lowercase; 40
// characters is exact, fewer is a prefix. An invalid one answers 400 naming the
// field, several matches answer `ambiguous: true` WITHOUT choosing one, and
// none answers 404.
export function useCommitTrace(hash) {
    const clean = String(hash ?? '').trim().toLowerCase();

    return useQuery({
        queryKey: commitKeys.trace(clean),
        queryFn: ({ signal }) => api.get(commitPaths.trace(clean), { signal }).then(r => r.data),
        enabled: /^[0-9a-f]{7,40}$/.test(clean),
        // A commit's chain does not change: the task may move on, but which
        // plan it came out of is settled.
        staleTime: 5 * 60_000,
        retry: false,
    });
}
